//
// Copyright © 2024 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { AttachedDoc, Blob, CollectionSize, Doc, Ref, Type, TypedSpace } from '@hcengineering/core'

import drive from './plugin'

/** @public */
export function TypeFileVersion (): Type<number> {
  return { _class: drive.class.TypeFileVersion, label: drive.string.FileVersion }
}

/** @public */
export interface Drive extends TypedSpace {}

/** @public */
export interface Resource extends Doc<Drive> {
  title: string
  parent: Ref<Resource>
  path: Ref<Resource>[]

  comments?: number

  // ugly but needed here to get version lookup work for Resource
  file?: Ref<FileVersion>

  /**
   * GKK: denormalised set of account uuids and group ids permitted to read
   * this resource, maintained by a server trigger whenever an Acl changes, a
   * Group's membership changes, or a resource is re-parented.
   *
   * This exists so visibility filtering stays a single indexed `$in` lookup,
   * the same cost profile as the existing space filter. The alternative —
   * post-filtering query results against the Acl table — breaks pagination
   * and result counts, and leaks existence through timing.
   *
   * Undefined means "inherit": no Acl applies anywhere up `path`, so the
   * containing Drive's role permissions decide, exactly as today.
   */
  aclReaders?: string[]
}

/** @public */
export interface Folder extends Resource {
  parent: Ref<Folder>
  path: Ref<Folder>[]

  file?: undefined
}

/** @public */
export interface File extends Resource {
  parent: Ref<Folder>
  path: Ref<Folder>[]

  file: Ref<FileVersion>
  versions: CollectionSize<FileVersion>
  version: number
}

/** @public */
export interface FileVersion extends AttachedDoc<File, 'versions', Drive> {
  /**
   * GKK: mirrors the owning File's readers. A FileVersion is an AttachedDoc,
   * not a Resource, so without this it carries no read set and the
   * visibility filter never applies to it - leaving the blob reachable to
   * anyone who can see the space, including an account added to that space
   * purely so one shared file is reachable.
   */
  aclReaders?: string[]
  title: string
  file: Ref<Blob>
  size: number
  type: string
  lastModified: number
  metadata?: Record<string, any>
  version: number
}
